import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import BottomNav from "@/components/BottomNav";
import Header from "@/components/Header";
import StatusBadge from "@/components/StatusBadge";
import { 
  Calendar, 
  Clock, 
  Phone, 
  MapPin, 
  Users, 
  Plus,
  Castle,
  PartyPopper,
  TrendingUp,
  AlertCircle,
  Globe,
  Search,
  X
} from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DashboardPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [todayReservations, setTodayReservations] = useState([]);
  const [upcomingReservations, setUpcomingReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, todayRes, upcomingRes] = await Promise.all([
        fetch(`${API}/reservations/stats`, { credentials: "include" }),
        fetch(`${API}/reservations/today`, { credentials: "include" }),
        fetch(`${API}/reservations/upcoming?limit=5`, { credentials: "include" }),
      ]);

      if (!statsRes.ok || !todayRes.ok || !upcomingRes.ok) {
        throw new Error("Failed to fetch data");
      }

      setStats(await statsRes.json());
      setTodayReservations(await todayRes.json());
      setUpcomingReservations(await upcomingRes.json());
    } catch (error) {
      toast.error("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (searchQuery.length < 2) {
      setSearchResults([]);
      return;
    }
    const timeout = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await fetch(`${API}/reservations/search?q=${encodeURIComponent(searchQuery)}`, { credentials: "include" });
        if (res.ok) {
          setSearchResults(await res.json());
        }
      } catch (e) {
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(timeout);
  }, [searchQuery]);

  if (loading) {
    return (
      <div className="min-h-screen festive-gradient flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-festive-pink border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen festive-gradient confetti-pattern page-container">
      <Header title="Tableau de bord" />
      
      <main className="p-4 space-y-6 max-w-6xl mx-auto">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <StatCard
            data-testid="stat-online-pending"
            icon={<Globe className="w-5 h-5" />}
            label="Demandes en ligne"
            value={stats?.by_status?.online_pending || 0}
            color="cyan"
            onClick={() => navigate("/online-pending")}
            pulse={stats?.unviewed_online > 0}
            badge={stats?.unviewed_online > 0 ? stats.unviewed_online : null}
          />
          <StatCard
            data-testid="stat-today"
            icon={<Calendar className="w-5 h-5" />}
            label="Aujourd'hui"
            value={stats?.today || 0}
            color="pink"
          />
          <StatCard
            data-testid="stat-upcoming"
            icon={<TrendingUp className="w-5 h-5" />}
            label="À venir"
            value={stats?.upcoming || 0}
            color="purple"
          />
          <StatCard
            data-testid="stat-options"
            icon={<AlertCircle className="w-5 h-5" />}
            label="En option"
            value={stats?.by_status?.option || 0}
            color="yellow"
          />
          <StatCard
            data-testid="stat-confirmed"
            icon={<PartyPopper className="w-5 h-5" />}
            label="Confirmées"
            value={stats?.by_status?.confirmed || 0}
            color="green"
          />
        </div>

        {/* Search Bar */}
        <div className="relative" data-testid="search-reservation">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              data-testid="search-input"
              type="text"
              placeholder="Rechercher par nom, téléphone ou email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-10 h-12 rounded-2xl border-pink-100 bg-white/80 backdrop-blur-sm focus:border-festive-pink focus:ring-festive-pink/20"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          
          {/* Search Results */}
          {searchQuery.length >= 2 && (
            <Card className="mt-2 card-festive overflow-hidden">
              <CardContent className="p-0">
                {searching ? (
                  <div className="p-4 text-center text-gray-500 text-sm">Recherche...</div>
                ) : searchResults.length === 0 ? (
                  <div className="p-4 text-center text-gray-500 text-sm">Aucun résultat pour "{searchQuery}"</div>
                ) : (
                  <div className="divide-y divide-pink-50">
                    <div className="px-4 py-2 bg-gray-50/50 text-xs text-gray-500 font-medium">
                      {searchResults.length} résultat{searchResults.length > 1 ? "s" : ""}
                    </div>
                    {searchResults.map((reservation) => (
                      <ReservationCard
                        key={reservation.id}
                        reservation={reservation}
                        onClick={() => navigate(`/reservations/${reservation.id}`)}
                        showDate
                      />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Quick Action */}
        <Button
          data-testid="new-reservation-btn"
          onClick={() => navigate("/reservations/new")}
          className="w-full h-14 bg-gradient-to-r from-festive-pink to-festive-purple hover:opacity-90 text-white font-semibold text-lg rounded-2xl shadow-button transition-transform hover:scale-[1.02] active:scale-[0.98]"
        >
          <Plus className="w-6 h-6 mr-2" />
          Nouvelle réservation
        </Button>

        {/* Today's Reservations */}
        <Card className="card-festive">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-lg font-heading">
              <div className="w-8 h-8 rounded-lg bg-festive-pink/10 flex items-center justify-center">
                <Clock className="w-4 h-4 text-festive-pink" />
              </div>
              Aujourd'hui
            </CardTitle>
          </CardHeader>
          <CardContent>
            {todayReservations.length === 0 ? (
              <EmptyState message="Aucune réservation aujourd'hui" />
            ) : (
              <div className="space-y-3">
                {todayReservations.map((reservation) => (
                  <ReservationCard
                    key={reservation.id}
                    reservation={reservation}
                    onClick={() => navigate(`/reservations/${reservation.id}`)}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Upcoming Reservations */}
        <Card className="card-festive">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-lg font-heading">
              <div className="w-8 h-8 rounded-lg bg-festive-purple/10 flex items-center justify-center">
                <Calendar className="w-4 h-4 text-festive-purple" />
              </div>
              Prochaines réservations
            </CardTitle>
          </CardHeader>
          <CardContent>
            {upcomingReservations.length === 0 ? (
              <EmptyState message="Aucune réservation à venir" />
            ) : (
              <div className="space-y-3">
                {upcomingReservations.map((reservation) => (
                  <ReservationCard
                    key={reservation.id}
                    reservation={reservation}
                    onClick={() => navigate(`/reservations/${reservation.id}`)}
                    showDate
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      <BottomNav />
    </div>
  );
}

const StatCard = ({ icon, label, value, color, onClick, pulse, badge, ...props }) => {
  const colorClasses = {
    pink: "from-festive-pink/20 to-festive-pink/5 text-festive-pink",
    purple: "from-festive-purple/20 to-festive-purple/5 text-festive-purple",
    yellow: "from-festive-yellow/30 to-festive-yellow/10 text-yellow-600",
    green: "from-green-200/50 to-green-100/30 text-green-600",
    cyan: "from-cyan-200/50 to-cyan-100/30 text-cyan-600",
  };

  return (
    <Card 
      className={`card-festive overflow-hidden ${onClick ? "cursor-pointer hover:scale-105 transition-transform" : ""} ${pulse ? "ring-2 ring-cyan-400 ring-offset-2" : ""}`}
      onClick={onClick}
      {...props}
    >
      <CardContent className={`p-4 bg-gradient-to-br ${colorClasses[color]} relative`}>
        {badge && (
          <span className="absolute top-2 right-2 min-w-[20px] h-5 px-1.5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center animate-pulse">
            {badge > 9 ? "9+" : badge}
          </span>
        )}
        <div className="flex items-center gap-2 mb-2">
          {icon}
          <span className="text-sm font-medium opacity-80">{label}</span>
        </div>
        <p className="text-3xl font-heading font-bold">{value}</p>
      </CardContent>
    </Card>
  );
};

const ReservationCard = ({ reservation, onClick, showDate }) => {
  const structureNames = {
    "aquatique": "Aquatique",
    "mickey-minnie": "Mickey & Minnie",
    "pat-patrouille": "Pat' Patrouille",
    "licorne": "Licorne",
    "super-mario": "Super Mario",
    "jungle": "Jungle",
  };

  const timeSlotNames = {
    "matin": "Matin (8h-13h)",
    "apres-midi": "Après-midi (14h-19h)",
    "journee": "Journée complète",
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("fr-FR", {
      weekday: "short",
      day: "numeric",
      month: "short",
    });
  };

  // Check if unviewed
  const isUnviewed = reservation.status === "DEMANDE_EN_LIGNE" && !reservation.viewed_at;

  return (
    <div
      data-testid={`reservation-card-${reservation.id}`}
      onClick={onClick}
      className={`p-4 rounded-2xl bg-white border shadow-sm hover:shadow-md transition-shadow cursor-pointer relative ${
        isUnviewed ? "border-cyan-300 ring-2 ring-cyan-200" : "border-pink-100"
      }`}
    >
      {/* Notification badge */}
      {isUnviewed && (
        <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full animate-pulse" />
      )}
      
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-festive-pink/20 to-festive-purple/20 flex items-center justify-center">
            <Castle className="w-5 h-5 text-festive-pink" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">
              {reservation.client_name}
            </h3>
            <p className="text-sm text-gray-500">
              {structureNames[reservation.structure_id] || reservation.structure_id}
            </p>
          </div>
        </div>
        <StatusBadge status={reservation.status} showNotification={isUnviewed} />
      </div>

      <div className="flex flex-wrap gap-3 text-sm text-gray-600">
        {showDate && (
          <div className="flex items-center gap-1">
            <Calendar className="w-4 h-4 text-festive-purple" />
            <span>{formatDate(reservation.date)}</span>
          </div>
        )}
        <div className="flex items-center gap-1">
          <Clock className="w-4 h-4 text-festive-pink" />
          <span>{timeSlotNames[reservation.time_slot] || reservation.time_slot}</span>
        </div>
        <a
          href={`tel:${reservation.client_phone}`}
          onClick={(e) => e.stopPropagation()}
          className="flex items-center gap-1 text-festive-pink hover:underline"
        >
          <Phone className="w-4 h-4" />
          <span>{reservation.client_phone}</span>
        </a>
      </div>
    </div>
  );
};

const EmptyState = ({ message }) => (
  <div className="py-8 text-center">
    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-pink-50 flex items-center justify-center">
      <Castle className="w-8 h-8 text-pink-300" />
    </div>
    <p className="text-gray-500">{message}</p>
  </div>
);
